// The reducer, wired to React, with the Save handed back to the caller.
//
// flowReducer returns a result rather than only a state, because a Save both
// changes the sheet and produces something the screen has to persist. useReducer
// cannot express that, so the effect is surfaced here instead of hidden.

import { useCallback, useRef, useState } from 'react'

import { currentMonthIndex } from 'src/utils/date/monthPeriods'

import { emptyState, flowReducer } from './entryFlow'
import type { FlowEvent, FlowState } from './entryFlow'
import type { EntryDraft } from './validation'

export interface SavedEntry {
  draft: EntryDraft
  /** Null for an entry being added for the first time. */
  id: string | null
}

export const useEntryFlow = (
  onSave: (saved: SavedEntry) => void,
  nowMonth: number = currentMonthIndex()
): [FlowState, (event: FlowEvent) => void] => {
  const [state, setState] = useState<FlowState>(emptyState)

  // Held in a ref so a caller does not have to memoise it to keep dispatch stable
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave

  // The reducer runs against the current state outside setState, so the save is
  // reported exactly once. Inside the updater it would fire again every time
  // React chose to re-invoke it — twice in StrictMode — and persist the entry
  // twice.
  const stateRef = useRef(state)
  stateRef.current = state

  const dispatch = useCallback(
    (event: FlowEvent) => {
      const result = flowReducer(stateRef.current, event, nowMonth)
      stateRef.current = result.state
      setState(result.state)
      if (result.saved) onSaveRef.current(result.saved)
    },
    [nowMonth]
  )

  return [state, dispatch]
}
